/**
 * Evidence-driven precision gate + candidate clustering.
 *
 * The accuracy benchmark makes the ceiling explicit and the project has always warned about it
 * (PLAN.md §165): with DATE-ONLY events a 2-hour search lands ~tens of minutes off (raahil wide
 * case: ~58.6 min), while a window already narrowed to the true region lands ~2.45 min. A precise
 * second is therefore only HONEST when the evidence can carry it — at least one event with a known
 * CLOCK TIME (`event_time`), ideally with a location so the Ruling-Planet / minute pathway engages
 * (lib/kp_scoring enables it only when `event_time` is present).
 *
 * This module is the single authority for "what resolution may we claim", so the UI can never
 * present a second-accurate result from date-only evidence. It reads only fields already on the
 * profile/candidates, so it adds no scan cost.
 *
 * Tiers (claimable resolution, NOT a quality score):
 *   • "window" — no clock-timed event. Date-only evidence fixes the rising sign / a rough region,
 *                 not the minute. Present a confidence WINDOW (the top-score cluster), never a second.
 *   • "minute" — exactly one clock-timed event. Aim for the minute; a second-level claim is not yet
 *                 supported. Add one more timed, located anchor to tighten.
 *   • "second" — two or more clock-timed, located anchor events. A second-level claim is supported.
 */

import { _parse_event_clock, optional_float } from "./scoring_common";
import { parseISODate, toOrdinal } from "./datetime";
import { _truthy, pythonRound } from "./pyutil";

export const TIER_WINDOW = "window";
export const TIER_MINUTE = "minute";
export const TIER_SECOND = "second";

// How many clock-timed (located) anchor events each tier requires.
export const MIN_TIMED_FOR_MINUTE = 1;
export const MIN_TIMED_LOCATED_FOR_SECOND = 2;

// Most events have NO recoverable clock time (nobody knows what time a grandparent died or a family
// trip left — those are arbitrary and must be left blank, never guessed). The anchors worth asking
// for are the ones a DOCUMENT can verify, so each suggestion names its document. Guessed times are
// worse than blank ones: the Ruling-Planet chart is cast for the event moment, so a wrong time
// corroborates the wrong ascendant with the full timed-event weight.
export const ANCHOR_EVENT_SUGGESTIONS: string[] = [
  "Child's exact birth time — on the birth certificate / hospital record",
  "Wedding ceremony / muhurta time — on the invitation card or priest's muhurta note",
  "Surgery or hospital admission time — on the discharge summary / hospital records",
  "Property registration appointment — on the sale deed / sub-registrar receipt",
  "Relocation flight or train departure — on the old ticket / booking email",
  "Job offer or admission call — timestamp on the offer email / call log",
];

// _truthy is the shared Python-truthiness helper from ./pyutil.

function _is_plain_object(value: any): value is Record<string, any> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** True when the event carries a parseable clock time (HH:MM or HH:MM:SS). */
export function event_has_clock(event: Record<string, any>): boolean {
  return _parse_event_clock(event["event_time"]) !== null;
}

/** True when the event carries explicit coordinates (so its own ascendant/RP chart is exact). */
export function event_has_location(event: Record<string, any>): boolean {
  return optional_float(event["event_lat"]) !== null && optional_float(event["event_lon"]) !== null;
}

function _dated_events(profile: Record<string, any>): Array<Record<string, any>> {
  return (_truthy(profile["events"]) ? profile["events"] : []).filter(
    (e: any) => _is_plain_object(e) && _truthy(e["start_date"]),
  );
}

/**
 * The resolution the profile's evidence can honestly support.
 *
 * Returns {tier, n_timed, n_timed_located, allow_exact_second, resolution_label, headline,
 * reason, missing}. `missing` is a short instruction for what to add to reach the next tier
 * (empty at the top tier).
 */
export function precision_tier(profile: Record<string, any>): Record<string, any> {
  const events = _dated_events(profile);
  const timed = events.filter((e) => event_has_clock(e));
  const timed_located = timed.filter((e) => event_has_location(e));
  const n_timed = timed.length;
  const n_tl = timed_located.length;

  let tier: string;
  if (n_tl >= MIN_TIMED_LOCATED_FOR_SECOND) {
    tier = TIER_SECOND;
  } else if (n_timed >= MIN_TIMED_FOR_MINUTE) {
    tier = TIER_MINUTE;
  } else {
    tier = TIER_WINDOW;
  }

  if (tier === TIER_SECOND) {
    return {
      tier: tier, n_timed: n_timed, n_timed_located: n_tl,
      allow_exact_second: true,
      resolution_label: "to the second",
      headline: `${n_tl} clock-timed, located anchor events — a second-level time is supported.`,
      reason:
        "Multiple events with a known clock time and place let the Ruling-Planet / " +
        "cuspal pathway resolve toward the exact second.",
      missing: "",
    };
  }
  if (tier === TIER_MINUTE) {
    const located_note = timed_located.length ? "" : " (add its location too, so its own chart is exact)";
    return {
      tier: tier, n_timed: n_timed, n_timed_located: n_tl,
      allow_exact_second: false,
      resolution_label: "to the minute",
      headline: "One clock-timed event — aim for the minute, not yet the second.",
      reason:
        "A single timed event tightens the window toward the minute" + located_note + "; " +
        "a second-level claim needs at least " +
        `${MIN_TIMED_LOCATED_FOR_SECOND} timed, located anchors.`,
      missing: "Add one more event with a known clock time and location to claim the exact second.",
    };
  }
  return {
    tier: tier, n_timed: n_timed, n_timed_located: n_tl,
    allow_exact_second: false,
    resolution_label: "as a window",
    headline: "Date-only evidence — this is a confidence window, not an exact time.",
    reason:
      "With no clock-timed event, the dated events fix the rising sign and a rough " +
      "region but cannot pin the minute (the documented date-only ceiling).",
    missing:
      "Add at least one event with a known clock time (and location) to time it to " +
      "the minute, then a second for the second.",
  };
}

export function _candidate_score(candidate: Record<string, any>): number | null {
  for (const key of ["deterministic_score", "calculated_score", "score"]) {
    const value = candidate[key];
    if (value !== null && value !== undefined) {
      const n = typeof value === "string" ? Number(value.trim() === "" ? NaN : value) : Number(value);
      if (Number.isNaN(n)) {
        continue; // Python: float() raised (TypeError, ValueError) → continue
      }
      return n;
    }
  }
  return null;
}

// Date-qualified candidate key ("YYYY-MM-DDTHH:MM:SS" or with a space) — the multi-day
// (induced-delivery) window's key form; see scoring.make_candidate_key.
const _DATED_LABEL_RE = /^(\d{4}-\d{2}-\d{2})[T ](.+)$/;

/** True when the candidate label carries an explicit calendar day. */
export function _label_is_dated(label: any): boolean {
  return _DATED_LABEL_RE.test(String(label).trim());
}

/**
 * Candidate label → seconds. A plain "HH:MM[:SS]" label returns its seconds-of-day exactly as
 * before. A DATE-QUALIFIED label ("YYYY-MM-DDTHH:MM:SS", multi-day windows) returns ABSOLUTE
 * seconds (proleptic day ordinal × 86400 + seconds of day), so two candidates at the same clock
 * on different days never alias — previously these keys failed to parse and every multi-day
 * candidate silently dropped out of `candidate_cluster`.
 */
export function _label_to_seconds(label: any): number | null {
  const raw = String(label).trim();
  const dated = _DATED_LABEL_RE.exec(raw);
  let parts: number[];
  try {
    parts = (dated ? dated[2] : raw)
      .split(":")
      .map((x) => {
        const s = x.trim();
        if (!/^[+-]?\d+$/.test(s)) {
          throw new Error(`invalid literal for int() with base 10: '${x}'`);
        }
        return Number(s);
      });
  } catch {
    return null;
  }
  while (parts.length < 3) {
    parts.push(0);
  }
  const [h, m, s] = parts.slice(0, 3);
  const seconds_of_day = h * 3600 + m * 60 + s;
  if (dated) {
    try {
      return toOrdinal(parseISODate(dated[1])) * 86400 + seconds_of_day;
    } catch {
      return null;
    }
  }
  return seconds_of_day;
}

export function _seconds_to_label(seconds: number): string {
  seconds = ((seconds % 86400) + 86400) % 86400;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
}

/**
 * The contiguous band of near-best candidates — what the evidence actually supports, as a
 * window rather than a single row.
 *
 * A candidate joins the cluster when its score is within `rel_points` of the top score AND it is
 * within `max_gap_minutes` of an already-included member (so a second, far-away peak of similar
 * score is NOT swallowed into one misleading band). Returns {top_time, low_time, high_time,
 * spread_minutes, n, members} or None if scores are unavailable.
 *
 * Times are treated on a 24h circle so a window straddling midnight reports correctly.
 */
export function candidate_cluster(
  candidates: Array<Record<string, any>>,
  rel_points = 2.0,
  max_gap_minutes = 20.0,
): Record<string, any> | null {
  const scored: Array<[string, number, number]> = [];
  for (const cand of candidates) {
    if (!_truthy(cand["time"])) continue;
    const score = _candidate_score(cand);
    const sec = _label_to_seconds(cand["time"]);
    if (score === null || sec === null) continue;
    scored.push([cand["time"], score, sec]);
  }
  if (!scored.length) {
    return null;
  }
  // Multi-day candidates carry date-qualified labels resolved to ABSOLUTE seconds by
  // `_label_to_seconds`; cluster those LINEARLY (no 24h circle — same clock on different days
  // must stay a full day apart), while plain time-of-day labels keep the circular treatment.
  const has_dates = scored.some(([tm]) => _label_is_dated(tm));
  // Python max(key=score) — FIRST maximal element.
  let top: [string, number, number] = scored[0];
  for (const t of scored) {
    if (t[1] > top[1]) top = t;
  }
  const [top_time, top_score, top_sec] = top;
  const near = scored.filter((t) => top_score - t[1] <= rel_points);

  // Grow a contiguous band outward from the top second, on the 24h circle, only across members
  // whose neighbour gap is within max_gap_minutes — so we report ONE region, not a fake span.
  const gap = max_gap_minutes * 60.0;
  const by_sec = [...near].sort((a, b) => a[2] - b[2]);
  const members: Array<[string, number, number]> = [[top_time, top_score, top_sec]];
  const included_secs = new Set<number>([top_sec]);

  const circular_gap = (a: number, b: number): number => {
    const d = (((Math.abs(a - b)) % 86400) + 86400) % 86400;
    return Math.min(d, 86400 - d);
  };
  const member_gap = has_dates ? (a: number, b: number): number => Math.abs(a - b) : circular_gap;

  let changed = true;
  while (changed) {
    changed = false;
    for (const [tm, sc, se] of by_sec) {
      if (included_secs.has(se)) {
        continue;
      }
      if (members.some(([, , m_se]) => member_gap(se, m_se) <= gap)) {
        members.push([tm, sc, se]);
        included_secs.add(se);
        changed = true;
      }
    }
  }

  const secs = [...included_secs].sort((a, b) => a - b);
  let low_time: string;
  let high_time: string;
  let spread_minutes: number;
  if (has_dates) {
    // Absolute (multi-day) mode: plain linear span; the band edges are the actual date-qualified
    // member labels so the day information survives into the report.
    const lo = members.reduce((a, b) => (b[2] < a[2] ? b : a));
    const hi = members.reduce((a, b) => (b[2] > a[2] ? b : a));
    low_time = lo[0];
    high_time = hi[0];
    spread_minutes = pythonRound((hi[2] - lo[2]) / 60.0, 1);
  } else {
    // Anchor the span at the top second and measure the farthest member each way on the circle.
    const deltas = secs.map((s) => ((((s - top_sec + 43200) % 86400) + 86400) % 86400) - 43200); // signed, -12h..+12h
    const min_delta = Math.min(...deltas);
    const max_delta = Math.max(...deltas);
    const low_sec = (((top_sec + min_delta) % 86400) + 86400) % 86400;
    const high_sec = (((top_sec + max_delta) % 86400) + 86400) % 86400;
    low_time = _seconds_to_label(low_sec);
    high_time = _seconds_to_label(high_sec);
    spread_minutes = pythonRound((max_delta - min_delta) / 60.0, 1);
  }
  // Degenerate guard: when the scores are FLAT, every scanned candidate chains into one band and
  // the "cluster" is just the scanned window handed back (e.g. an arbitrary 1440-min band on a
  // full-day scan) — an answer as wide as the question. Flag it (`meaningless`) instead of
  // presenting it as a finding; the UI may ignore the flag for now. A band that swallowed every
  // candidate but is still narrow (≤3h) remains a genuinely tight region and is NOT flagged.
  const meaningless = members.length === scored.length && scored.length > 1 && spread_minutes >= 180;
  return {
    top_time: top_time,
    low_time: low_time,
    high_time: high_time,
    spread_minutes: spread_minutes,
    n: members.length,
    members: members.map(([tm]) => tm).sort(),
    meaningless: meaningless,
  };
}
